/**
 * FFH-2372 — holding pen for events captured before consent settles.
 *
 * Capture is opted out until `setPostHogConsent(true)` runs after the
 * ~500ms CookieYes settle, and posthog-js silently discards `capture()`
 * calls while opted out. Any event fired on mount (funnel_started,
 * paywall_viewed) therefore raced the settle and lost. Events land here
 * until the first real consent read:
 *
 * - grant  → `flushPendingAnalyticsEvents` sends them, in order
 * - denial → `discardPendingAnalyticsEvents` drops them
 *
 * Both settle the queue for good: after the first consent read the SDK's
 * own opt-in/opt-out state is authoritative, and an event captured while
 * consent is denied must be dropped by the SDK, never parked here for a
 * later re-grant to leak out.
 */

type PendingAnalyticsEvent = {
  eventName: string;
  properties: Record<string, unknown>;
};

/**
 * The queue only ever holds the pre-settle window (~500ms of one page),
 * so a small cap is purely a leak guard for a consent banner that never
 * reports; excess events are dropped, matching what the SDK would do.
 */
const MAX_PENDING_ANALYTICS_EVENTS = 50;

let pendingEvents: PendingAnalyticsEvent[] = [];
let isQueueSettled = false;

export const enqueuePendingAnalyticsEvent = (
  eventName: string,
  properties: Record<string, unknown>
): void => {
  if (isQueueSettled) return;
  if (pendingEvents.length >= MAX_PENDING_ANALYTICS_EVENTS) return;

  pendingEvents.push({ eventName, properties });
};

export const flushPendingAnalyticsEvents = (
  send: (eventName: string, properties: Record<string, unknown>) => void
): void => {
  const eventsToSend = pendingEvents;
  pendingEvents = [];
  isQueueSettled = true;

  eventsToSend.forEach((event) => send(event.eventName, event.properties));
};

export const discardPendingAnalyticsEvents = (): void => {
  pendingEvents = [];
  isQueueSettled = true;
};

/** Test-only: reopen the queue, as on a fresh page load. */
export const resetPendingAnalyticsEventsForTesting = (): void => {
  pendingEvents = [];
  isQueueSettled = false;
};
